// src/components/users/userAiIntel.js
// --- UPDATE (User Management) ------------------------------------------------------------
// FlowSense AI for User Management (pure functions, no React):
//   suggestRole(jobTitle, department)  → role advisor while creating / editing a user
//   explainRole(role)                  → plain-language "what can this person do"
//   passwordStrength(pw)               → 0–4 meter + hints (mirrors the server policy)
//   answerUsersLocally(q, ctx)         → rules answers when the Ollama assistant is offline
//   buildUsersAiContext(...)           → compact snapshot sent to FlowSense AI (module users)
// --- END UPDATE ---------------------------------------------------------------------------
import { ROLE_META, ROLE_PERMISSIONS, PERMISSION_MODULES, PERMISSION_LEVELS, normalizeRole, roleMeta } from '../../config/roles';

const RULES = [
    // authority words first: "Helpdesk Supervisor" → supervisor, "Operations Manager" → manager
    [/\b(system ?admin(istrator)?|sysadmin|administrator)\b/i, 'admin', 'needs full system access (keep this to very few people)'],
    [/\b(manager|director|head of|chief|general manager|gm)\b/i, 'manager', 'runs operations, approvals and reports; manages users below manager'],
    [/\b(supervisor|foreman|team lead|crew lead|superintendent)\b/i, 'supervisor', 'dispatches crews, approves and receives escalations'],
    [/\b(contractor|vendor|sub-?contract(or)?|external)\b/i, 'contractor', 'works for an outside company — sees only the jobs assigned to them'],
    [/\b(help ?desk|call cent(er|re)|customer (service|care)|csr|agent|front desk|complaints?)\b/i, 'helpdesk', 'handles customer calls, emails and tickets'],
    [/\b(inspector|inspection|qa|quality|surveyor)\b/i, 'inspector', 'inspects roads, assets and wells and signs off completed work'],
    [/\b(tech(nician)?|plumber|fitter|mechanic|electrician|operator|crew|linesman|field worker|labou?rer)\b/i, 'tech', 'executes work orders in the field'],
    [/\b(clerk|dispatcher|scheduler|office|secretary|assistant|coordinator|store ?keeper|inventory|admin(istrative)? assistant)\b/i, 'staff', 'does office work: scheduling, inventory, notifications'],
];

/** Role advisor: the best-matching role for a job title / department, with the reason. */
export function suggestRole(jobTitle = '', department = '') {
    const text = `${jobTitle} ${department}`.trim();
    if (!text) return null;
    for (const [rx, role, why] of RULES) if (rx.test(text)) return { role, label: ROLE_META[role]?.label || role, why: `“${text}” ${why}.` };
    return null;
}

export function explainRole(role) {
    const r = normalizeRole(role);
    const m = roleMeta(r);
    const perms = ROLE_PERMISSIONS[r] || {};
    const can = PERMISSION_MODULES.filter((mod) => perms[mod.key] && perms[mod.key] !== '—');
    const strong = can.filter((mod) => ['full', 'manage'].includes(perms[mod.key])).map((mod) => mod.label);
    const own = can.filter((mod) => perms[mod.key] === 'own').map((mod) => mod.label);
    const none = PERMISSION_MODULES.filter((mod) => !perms[mod.key] || perms[mod.key] === '—').map((mod) => mod.label);
    return {
        role: r, label: m.label, description: m.description, level: m.level, field: !!m.field, external: !!m.external,
        summary: [
            m.description,
            strong.length ? `Manages: ${strong.join(', ')}.` : null,
            own.length ? `Only their own / assigned: ${own.join(', ')}.` : null,
            none.length && none.length < PERMISSION_MODULES.length ? `No access: ${none.join(', ')}.` : null,
        ].filter(Boolean).join(' '),
        modules: PERMISSION_MODULES.map((mod) => ({ ...mod, level: perms[mod.key] || '—', info: PERMISSION_LEVELS[perms[mod.key] || '—'] })),
    };
}

export function passwordStrength(pw = '', minLength = 10) {
    const p = String(pw);
    let score = 0;
    if (p.length >= minLength) score += 1;
    if (p.length >= minLength + 4) score += 1;
    if (/[a-z]/.test(p) && /[A-Z]/.test(p)) score += 1;
    if (/[0-9]/.test(p) && /[^A-Za-z0-9]/.test(p)) score += 1;
    const hints = [];
    if (p.length < minLength) hints.push(`${minLength - p.length} more character(s)`);
    if (!/[A-Za-z]/.test(p)) hints.push('a letter');
    if (!/[0-9]/.test(p)) hints.push('a number');
    if (!/[^A-Za-z0-9]/.test(p)) hints.push('a symbol makes it stronger');
    const ok = p.length >= minLength && /[A-Za-z]/.test(p) && /[0-9]/.test(p);
    return { score: ok ? Math.max(score, 1) : Math.min(score, 1), ok, label: !ok ? 'Too weak' : score >= 4 ? 'Strong' : score >= 3 ? 'Good' : 'Fair', hints };
}

/** Browser-side generator (the server can also generate one). */
export function generatePassword(length = 14) {
    const sets = ['ABCDEFGHJKLMNPQRSTUVWXYZ', 'abcdefghijkmnopqrstuvwxyz', '23456789', '!@#$%*?-'];
    const all = sets.join('');
    const rnd = (n) => { const a = new Uint32Array(1); (window.crypto || window.msCrypto).getRandomValues(a); return a[0] % n; };
    const chars = sets.map((s) => s[rnd(s.length)]);
    while (chars.length < length) chars.push(all[rnd(all.length)]);
    for (let i = chars.length - 1; i > 0; i--) { const j = rnd(i + 1); [chars[i], chars[j]] = [chars[j], chars[i]]; }
    return chars.join('');
}

const list = (arr, n = 8) => arr.slice(0, n).map((u) => u.display_name || u.username).join(', ') + (arr.length > n ? ` and ${arr.length - n} more` : '');

/** Rules answers for "who are the supervisors", "how many helpdesk agents", "what can a technician do" … */
export function answerUsersLocally(question, { users = [], insights = null } = {}) {
    const q = String(question || '').toLowerCase();
    const res = (answer, topic) => ({ answer, source: 'rules', topic });
    const active = users.filter((u) => u.status === 'active');
    const roleHit = Object.keys(ROLE_META).find((r) => {
        const m = ROLE_META[r];
        return q.includes(r) || q.includes(m.label.toLowerCase()) || q.includes(m.short.toLowerCase()) || (r === 'helpdesk' && /help ?desk|agent/.test(q)) || (r === 'tech' && /technician/.test(q));
    });
    if (roleHit && /(what|explain).*(can|do|access|permission)|permission|allowed/.test(q)) {
        const e = explainRole(roleHit);
        return res(`${e.label}: ${e.summary}`, 'role');
    }
    if (roleHit && /(who|list|show|which|how many|names?)/.test(q)) {
        const people = users.filter((u) => u.role === roleHit);
        const act = people.filter((u) => u.status === 'active');
        const label = ROLE_META[roleHit].label;
        if (!people.length) return res(`There are no ${label} accounts yet. Use "Add user" and pick ${label}.`, 'who');
        return res(`${act.length} active ${label}${act.length === 1 ? '' : 's'}${people.length !== act.length ? ` (${people.length - act.length} inactive / on leave)` : ''}: ${list(act.length ? act : people)}.`, 'who');
    }
    if (/(team|role).*(break ?down|mix|summary|count)|how many (users|people|accounts)|overview/.test(q)) {
        const parts = Object.keys(ROLE_META).map((r) => [ROLE_META[r].label, active.filter((u) => u.role === r).length]).filter(([, n]) => n);
        return res(`${active.length} active of ${users.length} accounts. ${parts.map(([l, n]) => `${l}: ${n}`).join(' · ')}.`, 'overview');
    }
    if (/inactive|leave|disabled|deactivat/.test(q)) {
        const off = users.filter((u) => u.status !== 'active');
        return res(off.length ? `${off.length} account(s) not active: ${list(off)}.` : 'Every account is active.', 'status');
    }
    if (/password|temporary|reset/.test(q)) {
        const tmp = users.filter((u) => u.must_change_password);
        return res(`${tmp.length ? `${tmp.length} account(s) still on a temporary password: ${list(tmp)}. ` : ''}To reset a password open the user's ⋮ menu → Reset password. FlowSense generates a strong temporary password (shown once) and the user must change it at their next sign-in.`, 'password');
    }
    if (/(busy|overload|workload|most (work|jobs))/.test(q)) {
        const field = active.filter((u) => u.is_field && u.open_work_orders != null).sort((a, b) => b.open_work_orders - a.open_work_orders);
        return res(field.length ? `Most open work: ${field.slice(0, 5).map((u) => `${u.display_name} (${u.open_work_orders})`).join(', ')}.` : 'No workload data yet.', 'workload');
    }
    if (/(create|add|new).*(user|account)|how do i add/.test(q)) {
        return res('Click "Add user", fill in the name, username and email, type the job title — FlowSense suggests the role — and leave the password empty to generate a temporary one. Administrators can give any role; managers can give roles below manager.', 'howto');
    }
    if (insights?.findings?.length && /(problem|issue|risk|fix|attention|recommend|health|score)/.test(q)) {
        return res([`Team health ${insights.score}/100 (${insights.grade}).`, ...insights.findings.slice(0, 6).map((f) => `• [${f.level}] ${f.title} — ${f.detail}`)].join('\n'), 'insights');
    }
    return res('Ask me e.g. "Who are the supervisors?", "How many helpdesk agents are active?", "What can a technician do?", "Who has the most open work?" or "What needs attention?".', 'help');
}

export function buildUsersAiContext({ users = [], insights = null, catalog = null } = {}) {
    const byRole = {};
    users.forEach((u) => { const r = u.role; byRole[r] = byRole[r] || { active: 0, other: 0, names: [] }; if (u.status === 'active') byRole[r].active += 1; else byRole[r].other += 1; if (byRole[r].names.length < 25) byRole[r].names.push(u.display_name || u.username); });
    return {
        page: 'User Management',
        roles: (catalog?.roles || Object.entries(ROLE_META).map(([id, m]) => ({ id, ...m }))).map((r) => ({ id: r.id, label: r.label, description: r.description, level: r.level })),
        team: byRole,
        health: insights ? { score: insights.score, grade: insights.grade, span_of_control: insights.span_of_control, findings: insights.findings.slice(0, 10).map((f) => ({ level: f.level, title: f.title, detail: f.detail })) } : null,
    };
}

export const USER_QUESTIONS = [
    'Who are the supervisors?',
    'How many helpdesk agents are active?',
    'What can a technician do?',
    'Who has the most open work?',
    'What needs attention?',
];
